import { useEffect, useState, JSX } from 'react';
import {
    useRecordContext
} from 'react-admin';
import {
    fetchOperatorConnection,
    operatorRequestHeaders,
    OperatorConnection
} from '../../../../providers/operatorConnection';
import {
    Box,
    Grid,
    IconButton
} from '@mui/material'
import {
    Refresh
} from '@mui/icons-material';
import { Operator } from "../../../../types/entities";

export const OperatorConnectionCount: () => JSX.Element = (): JSX.Element => {
    const record: Operator = useRecordContext();
    const [loading, setLoading] = useState<boolean>(true);
    const [connectionCount, setConnectionCount] = useState<number | undefined>(undefined);
    const [problem, setProblem] = useState<string>('');

    /**
     * HQAPI-76. Host and credential came off the Operator record, whose `token` field opened
     * every route on that Operator and never expired. A ticket is asked for per refresh now.
     *
     * HQ-57. A failure is shown, like the sibling panels do. This used to only log it, which left
     * "Connecting to Operator..." on screen for ever over a made-up count of 10.
     */
    async function fetchOperatorConnectionCount(): Promise<void> {
        setProblem('');
        try {
            const connection: OperatorConnection = await fetchOperatorConnection(record.id);
            const response: Response = await fetch(new Request(
                connection.host + '/system/status/active/connections',
                {
                    method: 'GET',
                    headers: operatorRequestHeaders(connection),
                }
            ));
            const body: any = await response.json();
            const reading: any = typeof body?.message === 'string' ? JSON.parse(body.message) : {};

            if (reading?.response === 'success') {
                setConnectionCount(reading['connection_count']);
            } else {
                setConnectionCount(undefined);
                setProblem('The Operator did not report a connection count.');
            }
        } catch (unreachable: any) {
            setConnectionCount(undefined);
            setProblem(unreachable?.message ?? 'The Operator could not be reached.');
        } finally {
            setLoading(false);
        }
    }

    async function refreshOperatorCount(): Promise<void> {
        setLoading(true);
        await fetchOperatorConnectionCount();
    }

    useEffect( (): void => {
        ( async (): Promise<void> => {
            await fetchOperatorConnectionCount();
            }
        )();
    }, [record] );

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                { (loading) &&
                    <Box>
                        <span>{ "Connecting to Operator..." }</span>
                    </Box>
                }
                { (!loading) &&
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingLeft: '8px' }}>
                        <Box>
                            <span >{ "Active Connections: " }</span>
                            <span style={ {   marginLeft: '30px'  } }  >{ connectionCount ?? '-' }</span>
                            { (problem !== '') &&
                                <Box sx={{ color: 'error.main', fontSize: '0.8rem', marginTop: '4px' }}>
                                    { problem }
                                </Box>
                            }
                        </Box>
                        <Box>
                            <IconButton title="Refresh status" onClick={ refreshOperatorCount }>
                                <Refresh />
                            </IconButton>
                        </Box>
                    </Box>
                }
            </Grid>
        </Grid>
    );
}
